import { inject, Injectable } from '@angular/core';
import { AmplitudeService } from '@brighthub/logging';

export interface OutcomeEventContext {
  guid: string;
  clientId?: number;
  caseId: number | null;
}
export type OutcomeEvent =
  | 'unlinked_outcomes_tab_viewed'
  | 'outcome_modal_opened'
  | 'outcome_updated'
  | 'outcome_link_saved'
  | 'outcome_relinked'
  | 'outcome_unlinked'
  | 'outcome_removed'
  | 'outcome_validation_failed'
  | 'outcome_save_failed'
  | 'outcome_modal_closed'
  | 'outcome_calculation_requested';

export interface OutcomeEventDetails {
  outcome_id?: string;
  outcome_type?: string;
  mode?: 'add' | 'edit';
  unlinked_count?: number;
  changed_fields?: string[];
  invalid_fields?: string[];
  case_id?: string | null;
  case_status?: string;
  option_ids?: string[];
  from_case_id?: string;
  to_case_id?: string;
  from_option_ids?: string[];
  to_option_ids?: string[];
  seconds_from_open?: number;
  operation?: 'update' | 'link' | 'relink' | 'unlink' | 'remove' | 'calculate';
  error_type?:
    | 'network'
    | 'conflict'
    | 'authorization'
    | 'validation'
    | 'server'
    | 'unknown';
  stage?: string;
  source_unlinked?: boolean;
  reason?: 'saved' | 'canceled' | 'discarded' | 'dismissed';
}

/** Identifiers, codes and field names only. Never pass outcome text or ATI amounts. */
@Injectable({ providedIn: 'root' })
export class OutcomeAnalytics {
  private readonly amplitude = inject(AmplitudeService);

  log(
    event: OutcomeEvent,
    context: OutcomeEventContext,
    details: OutcomeEventDetails = {},
  ): void {
    try {
      this.amplitude.logEvent(event, {
        app: 'brightnav',
        client_guid: context.guid,
        ...(context.clientId != null ? { client_id: context.clientId } : {}),
        entry_point: context.caseId === null ? 'unlinked_outcomes' : 'case',
        case_id: context.caseId === null ? null : String(context.caseId),
        ...details,
      });
    } catch {
      // Telemetry must never prevent persistence or turn success into a retry.
    }
  }
}

/** Classify structured status codes only; never send an error's message/body. */
export function outcomeErrorType(
  error: unknown,
): OutcomeEventDetails['error_type'] {
  let value = error as { status?: number; cause?: unknown } | null;
  for (
    let depth = 0;
    depth < 5 && value?.cause && value.cause !== value;
    depth++
  )
    value = value.cause as typeof value;
  const status = value?.status;
  if (status === 0) return 'network';
  if (status === 409) return 'conflict';
  if (status === 401 || status === 403) return 'authorization';
  if (status === 400 || status === 422) return 'validation';
  if (status != null && status >= 500) return 'server';
  return 'unknown';
}
